import throttle from "lodash/throttle";

class GoogleMapsStatic extends HTMLElement {
    static get observedAttributes() {
        return ["lat", "lng", "zoom", "key", "show-marker", "alt"];
    }

    constructor() {
        super();

        this.gmapSettings = null;
        this.imageEl = null;
        this.mapStyle = [
            {
                elementType: "geometry",
                stylers: [
                    {
                        color: "#f5f5f5",
                    },
                ],
            },
            {
                elementType: "labels.icon",
                stylers: [
                    {
                        visibility: "off",
                    },
                ],
            },
            {
                elementType: "labels.text.fill",
                stylers: [
                    {
                        color: "#333333",
                    },
                    {
                        lightness: 40,
                    },
                ],
            },
            {
                elementType: "labels.text.stroke",
                stylers: [
                    {
                        color: "#ffffff",
                    },
                ],
            },
            {
                featureType: "administrative.land_parcel",
                elementType: "labels.text.fill",
                stylers: [
                    {
                        color: "#bdbdbd",
                    },
                ],
            },
            {
                featureType: "poi",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#eeeeee",
                    },
                ],
            },
            {
                featureType: "poi.park",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#e5e5e5",
                    },
                ],
            },
            {
                featureType: "road",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#ffffff",
                    },
                ],
            },
            {
                featureType: "road.highway",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#dadada",
                    },
                ],
            },
            {
                featureType: "transit.line",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#e5e5e5",
                    },
                ],
            },
            {
                featureType: "transit.station",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#eeeeee",
                    },
                ],
            },
            {
                featureType: "water",
                elementType: "geometry",
                stylers: [
                    {
                        color: "#c9c9c9",
                    },
                ],
            },
        ];
    }

    connectedCallback() {
        this.lat = this.getAttribute("lat");
        this.lng = this.getAttribute("lng");
        this.zoom = this.getAttribute("zoom") ?? 14;
        this.key = this.getAttribute("key");
        this.showMarker = this.getAttribute("show-marker") ?? true;
        this.markerIconUrl = this.getAttribute("marker-icon-url");
        this.alt = this.getAttribute("alt") ?? "Map";

        if (!this.lat || !this.lng || !this.key) {
            console.error("[google-maps-static]: Missing required attributes");
            return;
        }

        this.init();
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue !== newValue) {
            this[name] = newValue;

            this.generateStaticMap();
        }
    }

    init() {
        this.createImageElement();

        this.generateStaticMap();

        window.addEventListener(
            "resize",
            throttle(this.generateStaticMap.bind(this), 200),
        );
    }

    createImageElement() {
        this.imageEl = this.querySelector("img");
 
        if (!this.imageEl) {
            // Create one if it doesn't exist
            this.imageEl = document.createElement("img");
            this.imageEl.loading = "lazy";
            this.imageEl.alt = this.alt ?? "Map";
            this.appendChild(this.imageEl);
        }
    }

    displayMapImage() {
        this.imageEl.src = this.mapUrl;
    }

    generateStaticMap() {
        if (this.imageEl === null) {
            return;
        }

        const baseUrl = "https://maps.googleapis.com/maps/api/staticmap";

        let { width, height } = this.imageEl.getBoundingClientRect();
        const aspectRatio = width / height;

        if (width > 640) {
            width = 640;
            height = width / aspectRatio;
        }

        const params = {
            center: `${this.lat},${this.lng}`,
            zoom: this.zoom,
            key: this.key,
            scale: 2,
        };

        if (this.showMarker) {
            if (this.markerIconUrl) {
                params.markers = `icon:${this.markerIconUrl}`;
            } else {
                params.markers = `color:0xF60E5F|size:mid`; // Default marker
            }

            params.markers += `|${this.lat},${this.lng}`;
        }

        params.size = `${Math.round(width)}x${Math.round(height)}`;

        const queryString = Object.keys(params)
            .map((key) => key + "=" + encodeURIComponent(params[key]))
            .join("&");

        // Build map URL
        let mapUrl = `${baseUrl}?${queryString}`;

        mapUrl += `&${this.getStyleArray(this.mapStyle)}`;

        this.mapUrl = mapUrl;
        this.displayMapImage();
    }

    getStyleArray(styles) {
        var result = [];
        styles.forEach(function (v, i, a) {
            var style = "";
            if (v.stylers.length > 0) {
                // Needs to have a style rule to be valid.
                style +=
                    (v.hasOwnProperty("featureType")
                        ? "feature:" + v.featureType
                        : "feature:all") + "|";
                style +=
                    (v.hasOwnProperty("elementType")
                        ? "element:" + v.elementType
                        : "element:all") + "|";
                v.stylers.forEach(function (val, i, a) {
                    var propertyname = Object.keys(val)[0];
                    var propertyval = val[propertyname]
                        .toString()
                        .replace("#", "0x");
                    style += propertyname + ":" + propertyval + "|";
                });
            }
            result.push("style=" + encodeURIComponent(style));
        });

        return result.join("&");
    }
}

customElements.define("google-maps-static", GoogleMapsStatic);
